import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { Endpoints } from './endpoints';
import { ApiClient } from './api-client';
import { Account } from '../models/transactions/account';
import { catchError, map } from 'rxjs/operators';
import { CustomError } from '../models/shared/custom-error';
import { Observable, of, throwError } from 'rxjs';
import { TransactionEntry } from '../models/transactions/transaction-entry';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { ODataResponse } from '../models/protocols/odata-response';
import { MemberAccount } from '../models/transactions/member-account';
import { AccountTypeIdEnum } from '../models/transactions/enum/account-type-id.enum';
import { AccountStatusEnum } from '../models/transactions/enum/account-status.enum';
import { MemberAccountFundAllocation } from '../models/transactions/member-account-fund-allocation';
import { InvestmentInstructions } from '../models/transactions/investment-instructions';
import { MemberAccountContributionBalance } from '../models/transactions/member-account-contribution-balance';
import { MemberAccountFundDetails } from '../models/transactions/member-account-fund-details';
import { VbWithdrawalAmountRequest } from '../models/shared/requests/vb-withdrawal-amount-request';
import { VbWithdrawalAmount } from '../models/shared/vb-withdrawal-amount';
import { VbAccountActivityBreakdown } from '../models/transactions/vb-account-activity-breakdown';
import { VariableBenefitAccountDetails } from '../models/transactions/variable-benefit-account-details';
import { MemberVbAccountEstimatedAnnualPaymentTotal } from '../models/transactions/member-vb-account-estimated-annual-payment-total';
import { MemberVbAccountEstimatedAnnualPaymentTotalRequest } from '../models/transactions/member-vb-account-estimated-annual-payment-total-request';
import { MpMemberVbAccountEstimatedAnnualPaymentTotal } from '../models/transactions/mp-member-vb-account-estimated-annual-payment-total';
import { BankAccount } from '../views/banking/models/bank-account';
import { IpMemberVbAccountEstimatedAnnualPaymentTotalRequest } from '../models/transactions/ip-member-vb-account-estimated-annual-payment-total-request';
import { OutboundPaymentChangeDate } from '../models/transactions/outbound-payment-change-date';
import { VbAccountUpcomingPayment } from '../models/transactions/vb-account-upcoming-payment';
import { AnnuityPensionAccountDetails } from '../models/transactions/annuity-pension-account-details';
import { UpdateAnnuityAccountDepositInstructionsRequest } from '../models/transactions/requests/update-annuity-account-deposit-instructions-request';
import { IpUpdateAnnuityAccountDepositInstructionsRequest } from '../models/transactions/requests/ip-update-annuity-account-deposit-instructions-request';
import { IpTerminateAnnuityPaymentInstructionRequest } from '../models/transactions/requests/ip-terminate-annuity-payment-instruction-request';
import { IpAnnuityPaymentInstructionStatusChangeRequest } from '../models/transactions/requests/ip-annuity-payment-instruction-status-change-request';
import { IpEngageAnnuityJlsPropertiesRequest } from '../models/transactions/requests/ip-engage-annuity-jls-properties-request';
import { IpSplitAnnuityRequest } from '../models/transactions/requests/ip-split-annuity-request';
import { AnnuityPensionAccountActionResponse } from '../models/transactions/annuity-pension-account-action-response';
import { AnnuityEntitlement } from '../models/transactions/annuity-entitlement';
import { WithdrawalInstruction } from '../models/submissions/withdrawal-instruction';
import { CreateAnnuityCalculationRequest } from '../models/annuities/requests/create-annuity-calculation-request';
import {
  JointLifeAnnuityCalculationResponse,
  LifeOnlyAnnuityCalculationResponse
} from '../models/annuities/dto/annuity-calculation-response';
import { AnnuityOperationalConversionRateResponse } from '../models/annuities/dto/annuity-operational-conversion-rate-response';
import { SmallPensionMonthlyLimit } from '../models/annuities/dto/small-pension-monthly-limit';
import { TimeZoneUtils } from '../utils/time-zone-utils';
import {
  mockAttachSpousalRolloverLinks,
  mockAttachStatusChange,
  mockChangeAccountStatus,
  mockCreateSpousalVariableBenefitAccount,
  mockCreateTransferInAccount,
  mockGetSpousalVariableBenefitSourceAccount,
  mockGetTransferInAccounts
} from './account-api.mocks';
import { MemberRecord } from '../models/users/dto/member-record';
import { MemberUserSearchResult } from '../models/users/dto/member-user-search-result';
import { SpousalVariableBenefitSourceAccount } from '../models/transactions/spousal-variable-benefit-source-account';

export interface MemberAccountsFilter {
  accountTypeId?: AccountTypeIdEnum;
  excludeStruck?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AccountAPI implements LoggableAPI {
  private apiClient = inject(ApiClient);

  serviceName = 'AccountAPI';

  public getAccountById(id: number): Observable<Account> {
    const url = Endpoints.getAccountById(id);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('Type');
    return this.apiClient.getOdataObj(url, Account, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getAccountEntries(accountId: number) {
    const url = Endpoints.getAccountEntries(accountId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('Code, LegacyCode, Status, Currency');
    oDataQueryOptions.setCount(true);
    return this.apiClient.getOdata(url, TransactionEntry, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetAccounts(opts: ODataQueryOptions): Observable<ODataResponse<MemberAccount>> {
    const url = Endpoints.mpGetAccounts();
    return this.apiClient.getOdata(url, MemberAccount, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetInvestmentInstructionsForAccountType(
    accountTypeId: AccountTypeIdEnum,
    excludeZeroPercentFunds = false
  ): Observable<InvestmentInstructions> {
    const url = Endpoints.mpGetInvestmentInstructionsForAccountType(accountTypeId, excludeZeroPercentFunds);
    return this.apiClient.getObj<InvestmentInstructions>(InvestmentInstructions, url, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetFundAllocationsForAccountType(
    accountTypeId: AccountTypeIdEnum,
    excludeCash: boolean = false
  ): Observable<MemberAccountFundAllocation[]> {
    const url = Endpoints.mpGetFundAllocationsForAccountType(accountTypeId, excludeCash);
    return this.apiClient.getArr(MemberAccountFundAllocation, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMPContributionAccountBalances(): Observable<MemberAccountContributionBalance[]> {
    const url = Endpoints.getMPContributionAccountBalances();
    return this.apiClient.getArr(MemberAccountContributionBalance, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetFundDetailsForAccountType(accountTypeId: AccountTypeIdEnum): Observable<MemberAccountFundDetails[]> {
    const url = Endpoints.mpGetFundDetailsForAccountType(accountTypeId);
    return this.apiClient.getArr(MemberAccountFundDetails, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetTransactionEntriesForAccountType(
    accountTypeId: AccountTypeIdEnum,
    opts = new ODataQueryOptions()
  ): Observable<ODataResponse<TransactionEntry>> {
    const url = Endpoints.mpGetTransactionEntriesForAccountType(accountTypeId);
    return this.apiClient.getOdata(url, TransactionEntry, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberAccountById(memberId: number, accountId: number): Observable<MemberAccount> {
    const url = Endpoints.ipGetMemberAccountById(memberId, accountId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand(
      'Type, Status, ContributionAccountDetails, VariableBenefitAccountDetails, AnnuityAccountDetails'
    );
    return this.apiClient.getOdataObj(url, MemberAccount, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberAccountFundDetails(memberId: number, accountId: number): Observable<MemberAccountFundDetails[]> {
    const url = Endpoints.ipGetMemberAccountFundDetails(memberId, accountId);
    return this.apiClient.getArr(MemberAccountFundDetails, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getIPMemberAccountTypeBalancesByFund(
    memberId: number,
    accountTypeId: AccountTypeIdEnum
  ): Observable<MemberAccountFundAllocation[]> {
    const url = Endpoints.getIPMemberAccountTypeBalancesByFund(memberId, accountTypeId);
    return this.apiClient.getArr<MemberAccountFundAllocation>(MemberAccountFundAllocation, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberAccounts(
    memberId: number,
    { accountTypeId, excludeStruck }: MemberAccountsFilter = {}
  ): Observable<ODataResponse<MemberAccount>> {
    const url = Endpoints.ipGetMemberAccounts(memberId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand(
      'Type, Status, ContributionAccountDetails, VariableBenefitAccountDetails, AnnuityAccountDetails($expand=AnnuityType, AnnuityGuaranteePeriod)'
    );
    oDataQueryOptions.setCount(true);
    const filters: string[] = [];
    if (accountTypeId) {
      filters.push(`Type/Id eq ${accountTypeId}`);
    }
    if (excludeStruck) {
      filters.push(`StatusId ne ${AccountStatusEnum.Struck}`);
    }
    if (filters.length) {
      oDataQueryOptions.setFilter(filters.join(' and '));
    }
    return this.apiClient.getOdata(url, MemberAccount, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // TODO: SPP-3367 call the real endpoint to create a Transfer-In Account once the backend exists.
  public ipCreateMemberTransferInAccount(memberId: number): Observable<MemberAccount> {
    return of(mockCreateTransferInAccount(memberId)).pipe(
      map(account => window.injector.Deserialize.instanceOf(MemberAccount, account))
    );
  }

  // TODO: SPP-3367 delete once ipGetMemberAccounts returns Transfer-In Accounts.
  public ipGetMemberTransferInAccounts(memberId: number): Observable<MemberAccount[]> {
    return of(mockGetTransferInAccounts(memberId)).pipe(
      map(accounts => accounts.map(account => window.injector.Deserialize.instanceOf(MemberAccount, account)))
    );
  }

  // TODO: SPP-3388 call GET internal/members/{memberId}/spousalvariablebenefit/sourceaccount with only the member's id
  // once the backend is deployed.
  public ipGetSpousalVariableBenefitSourceAccount(
    member: MemberRecord,
    vbAccount: MemberAccount | undefined
  ): Observable<SpousalVariableBenefitSourceAccount> {
    return of(mockGetSpousalVariableBenefitSourceAccount(member, vbAccount)).pipe(
      map(sourceAccount => window.injector.Deserialize.instanceOf(SpousalVariableBenefitSourceAccount, sourceAccount))
    );
  }

  // TODO: SPP-3390 delete once the account reads return spousalRolloverAccount and spousalRolloverSource.
  public attachSpousalRolloverLinks(account: MemberAccount): MemberAccount {
    return mockAttachSpousalRolloverLinks(account);
  }

  // TODO: SPP-3389 call POST internal/members/{memberId}/spousalvariablebenefitaccount with
  // { memberId, sourceAccountId, successorMemberId: successor.id } once the backend is deployed.
  public ipCreateSpousalVariableBenefitAccount(
    memberId: number,
    sourceAccountId: number,
    successor: MemberUserSearchResult
  ): Observable<void> {
    return of(mockCreateSpousalVariableBenefitAccount(sourceAccountId, successor));
  }

  // TODO: SPP-3441 call PUT internal/members/{memberId}/accounts/{accountId}/suspend once the backend exists.
  public ipSuspendMemberAccount(memberId: number, account: MemberAccount): Observable<void> {
    return of(mockChangeAccountStatus(account, AccountStatusEnum.Suspended));
  }

  // TODO: SPP-3441 call PUT internal/members/{memberId}/accounts/{accountId}/liftsuspension once the backend exists.
  public ipLiftMemberAccountSuspension(memberId: number, account: MemberAccount): Observable<void> {
    return of(mockChangeAccountStatus(account, AccountStatusEnum.Active));
  }

  // TODO: SPP-3441 call PUT internal/members/{memberId}/accounts/{accountId}/strike once the backend exists.
  public ipStrikeMemberAccount(memberId: number, account: MemberAccount): Observable<void> {
    return of(mockChangeAccountStatus(account, AccountStatusEnum.Struck));
  }

  // TODO: SPP-3441 delete once the account reads return the changed status and statusChangedDate.
  public attachStatusChange(account: MemberAccount): MemberAccount {
    return mockAttachStatusChange(account);
  }

  public ipGetMemberAccountsWithType(
    memberId: number,
    { excludeStruck }: Pick<MemberAccountsFilter, 'excludeStruck'> = {}
  ): Observable<ODataResponse<MemberAccount>> {
    const url = Endpoints.ipGetMemberAccounts(memberId);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('Type');
    if (excludeStruck) {
      oDataQueryOptions.setFilter(`StatusId ne ${AccountStatusEnum.Struck}`);
    }
    return this.apiClient.getOdata(url, MemberAccount, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetAccountDetailsForVbAccount(memberId: number): Observable<VariableBenefitAccountDetails> {
    const url = Endpoints.ipGetAccountDetailsForVbAccount(memberId);
    return this.apiClient.getObj<VariableBenefitAccountDetails>(VariableBenefitAccountDetails, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetAnnuityAccountDetails(accountId: number): Observable<AnnuityPensionAccountDetails> {
    const url = Endpoints.mpGetAnnuityAccountDetails(accountId);
    return this.apiClient.getObj<AnnuityPensionAccountDetails>(AnnuityPensionAccountDetails, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberAnnuityAccountDetails(
    memberId: number,
    accountId: number
  ): Observable<AnnuityPensionAccountDetails> {
    const url = Endpoints.ipGetMemberAnnuityAccountDetails(memberId, accountId);
    return this.apiClient.getObj<AnnuityPensionAccountDetails>(AnnuityPensionAccountDetails, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetAnnuityEntitlements(): Observable<AnnuityEntitlement[]> {
    const url = Endpoints.mpGetAnnuityEntitlements();
    return this.apiClient.getArr(AnnuityEntitlement, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberAnnuityEntitlements(memberId: number): Observable<AnnuityEntitlement[]> {
    const url = Endpoints.ipGetMemberAnnuityEntitlements(memberId);
    return this.apiClient.getArr(AnnuityEntitlement, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberAnnuityAccountDepositInstructions(
    req: IpUpdateAnnuityAccountDepositInstructionsRequest
  ): Observable<AnnuityPensionAccountDetails> {
    const url = Endpoints.ipUpdateMemberAnnuityPaymentInstructionDepositInstructions(
      req.memberId,
      req.accountId,
      req.paymentInstructionId
    );
    return this.apiClient.putObj<AnnuityPensionAccountDetails>(AnnuityPensionAccountDetails, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipTerminateMemberAnnuityPaymentInstruction(
    req: IpTerminateAnnuityPaymentInstructionRequest
  ): Observable<AnnuityPensionAccountActionResponse> {
    const url = Endpoints.ipTerminateMemberAnnuityPaymentInstruction(
      req.memberId,
      req.accountId,
      req.paymentInstructionId
    );
    return this.apiClient
      .putObj<AnnuityPensionAccountActionResponse>(AnnuityPensionAccountActionResponse, url, req)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipSuspendMemberAnnuityPaymentInstruction(
    req: IpAnnuityPaymentInstructionStatusChangeRequest
  ): Observable<AnnuityPensionAccountActionResponse> {
    const url = Endpoints.ipSuspendMemberAnnuityPaymentInstruction(
      req.memberId,
      req.accountId,
      req.paymentInstructionId
    );
    return this.apiClient
      .putObj<AnnuityPensionAccountActionResponse>(AnnuityPensionAccountActionResponse, url, req)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipResumeMemberAnnuityPaymentInstruction(
    req: IpAnnuityPaymentInstructionStatusChangeRequest
  ): Observable<AnnuityPensionAccountActionResponse> {
    const url = Endpoints.ipResumeMemberAnnuityPaymentInstruction(
      req.memberId,
      req.accountId,
      req.paymentInstructionId
    );
    return this.apiClient
      .putObj<AnnuityPensionAccountActionResponse>(AnnuityPensionAccountActionResponse, url, req)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipEngageMemberAnnuityJlsProperties(
    req: IpEngageAnnuityJlsPropertiesRequest
  ): Observable<AnnuityPensionAccountActionResponse> {
    const url = Endpoints.ipEngageMemberAnnuityJlsProperties(req.memberId, req.accountId);
    return this.apiClient
      .postObj<AnnuityPensionAccountActionResponse>(AnnuityPensionAccountActionResponse, url, req)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipSplitMemberAnnuity(req: IpSplitAnnuityRequest): Observable<AnnuityPensionAccountActionResponse> {
    const url = Endpoints.ipSplitMemberAnnuity(req.memberId, req.accountId);
    return this.apiClient
      .postObj<AnnuityPensionAccountActionResponse>(AnnuityPensionAccountActionResponse, url, req)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public mpUpdateAnnuityAccountDepositInstructions(
    req: UpdateAnnuityAccountDepositInstructionsRequest
  ): Observable<AnnuityPensionAccountDetails> {
    const url = Endpoints.mpUpdateAnnuityPaymentInstructionDepositInstructions(req.accountId, req.paymentInstructionId);
    return this.apiClient.putObj<AnnuityPensionAccountDetails>(AnnuityPensionAccountDetails, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberFundAllocations(
    memberId: number,
    accountIds: number[],
    excludeCash: boolean
  ): Observable<MemberAccountFundAllocation[]> {
    const url = Endpoints.ipGetMemberAccountAllocationOfFunds(memberId);
    const params = `?accountIds=${accountIds.join(',')}&excludeCash=${excludeCash}`;
    return this.apiClient.getArr(MemberAccountFundAllocation, url + params).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getIPContributionBalancesForAccount(
    memberId: number,
    accountId: number
  ): Observable<MemberAccountContributionBalance[]> {
    const url = Endpoints.getIPContributionBalancesForAccount(memberId, accountId);
    return this.apiClient.getArr(MemberAccountContributionBalance, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberContributionAccountInvestmentInstructions(
    memberId: number,
    excludeZeroPercentFunds = false
  ): Observable<InvestmentInstructions> {
    const url =
      Endpoints.ipGetMemberContributionAccountInvestmentInstructions(memberId) +
      `&excludeZeroPercentFunds=${excludeZeroPercentFunds}`;
    return this.apiClient.getObj<InvestmentInstructions>(InvestmentInstructions, url, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberInvestmentInstructionsForAccountType(
    memberId: number,
    accountTypeId: AccountTypeIdEnum,
    excludeZeroPercentFunds = false
  ): Observable<InvestmentInstructions> {
    const url =
      Endpoints.ipGetMemberInvestmentInstructionsForAccountType(memberId, accountTypeId) +
      `&excludeZeroPercentFunds=${excludeZeroPercentFunds}`;
    return this.apiClient.getObj<InvestmentInstructions>(InvestmentInstructions, url, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetAccountDetailsForVbAccount(): Observable<VariableBenefitAccountDetails> {
    const url = Endpoints.mpGetAccountDetailsForVbAccount();
    return this.apiClient.getObj<VariableBenefitAccountDetails>(VariableBenefitAccountDetails, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpConfirmDefaultInvestmentInstructions(): Observable<any> {
    const url = Endpoints.mpConfirmDefaultInvestmentInstructions();
    return this.apiClient.simpleBodylessPut(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Portal Agnostic
  public getVbMinMaxWithdrawalAmounts(req: VbWithdrawalAmountRequest): Observable<VbWithdrawalAmount> {
    const url = Endpoints.getVbMinMaxWithdrawalAmounts();
    return this.apiClient.postObj(VbWithdrawalAmount, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberVbAccountActivityBreakdown(memberId: number): Observable<VbAccountActivityBreakdown> {
    const url = Endpoints.ipGetMemberVbAccountActivityBreakdown(memberId);
    return this.apiClient.getObj<VbAccountActivityBreakdown>(VbAccountActivityBreakdown, url, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetVbAccountActivityBreakdown(): Observable<VbAccountActivityBreakdown> {
    const url = Endpoints.mpGetVbAccountActivityBreakdown();
    return this.apiClient.getObj<VbAccountActivityBreakdown>(VbAccountActivityBreakdown, url, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEstimatedAnnualPaymentTotalForVbAccount(
    req: IpMemberVbAccountEstimatedAnnualPaymentTotalRequest
  ): Observable<MemberVbAccountEstimatedAnnualPaymentTotal> {
    const url = Endpoints.ipGetEstimatedAnnualPaymentTotalForVbAccount(req.memberId);
    return this.apiClient.postObj(MemberVbAccountEstimatedAnnualPaymentTotal, url, req, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetUpcomingPaymentsForVbAccount(
    memberId: number,
    paymentCount: number
  ): Observable<VbAccountUpcomingPayment[]> {
    const url = Endpoints.ipGetUpcomingPaymentsForVbAccount(memberId);
    return this.apiClient.getArr(VbAccountUpcomingPayment, `${url}?paymentCount=${paymentCount}`).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetUpcomingPaymentsForVbAccount(paymentCount: number): Observable<VbAccountUpcomingPayment[]> {
    const url = Endpoints.mpGetUpcomingPaymentsForVbAccount();
    return this.apiClient.getArr(VbAccountUpcomingPayment, `${url}?paymentCount=${paymentCount}`).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetWithdrawalInstructionsForVbAccount(
    memberId: number,
    excludeZeroPercentFunds = false
  ): Observable<WithdrawalInstruction[]> {
    const url = Endpoints.ipGetWithdrawalInstructionsForVbAccount(memberId, excludeZeroPercentFunds);
    return this.apiClient.getArr(WithdrawalInstruction, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetWithdrawalInstructionsForVbAccount(excludeZeroPercentFunds = false): Observable<WithdrawalInstruction[]> {
    const url = Endpoints.mpGetWithdrawalInstructionsForVbAccount(excludeZeroPercentFunds);
    return this.apiClient.getArr(WithdrawalInstruction, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetEstimatedAnnualPaymentTotalForVbAccount(
    req: MemberVbAccountEstimatedAnnualPaymentTotalRequest
  ): Observable<MpMemberVbAccountEstimatedAnnualPaymentTotal> {
    const url = Endpoints.mpGetEstimatedAnnualPaymentTotalForVbAccount();
    return this.apiClient.postObj(MpMemberVbAccountEstimatedAnnualPaymentTotal, url, req, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberBankAccountById(
    memberId: number,
    bankAccountId: number,
    opts = new ODataQueryOptions()
  ): Observable<BankAccount> {
    const url = Endpoints.ipGetMemberBankAccountById(memberId, bankAccountId);
    return this.apiClient.getOdataObj<BankAccount>(url, BankAccount, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetVbOutboundPaymentCutoffDate(): Observable<OutboundPaymentChangeDate> {
    const url = Endpoints.ipGetVbOutboundPaymentCutoffDate();
    return this.apiClient.getObj<OutboundPaymentChangeDate>(OutboundPaymentChangeDate, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetVbOutboundPaymentCutoffDate(): Observable<OutboundPaymentChangeDate> {
    const url = Endpoints.mpGetVbOutboundPaymentCutoffDate();
    return this.apiClient.getObj<OutboundPaymentChangeDate>(OutboundPaymentChangeDate, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getLifeOnlyAnnuityCalculation(
    req: CreateAnnuityCalculationRequest
  ): Observable<LifeOnlyAnnuityCalculationResponse> {
    const url = Endpoints.getLifeOnlyAnnuityCalculation();
    return this.apiClient.postObj(LifeOnlyAnnuityCalculationResponse, url, req, this.buildTimeZoneHeaders()).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getJointLifeAnnuityCalculation(
    req: CreateAnnuityCalculationRequest
  ): Observable<JointLifeAnnuityCalculationResponse> {
    const url = Endpoints.getJointLifeAnnuityCalculation();
    return this.apiClient.postObj(JointLifeAnnuityCalculationResponse, url, req, this.buildTimeZoneHeaders()).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRefundLifeAnnuityCalculation(
    req: CreateAnnuityCalculationRequest
  ): Observable<LifeOnlyAnnuityCalculationResponse> {
    const url = Endpoints.getRefundLifeAnnuityCalculation();
    return this.apiClient.postObj(LifeOnlyAnnuityCalculationResponse, url, req, this.buildTimeZoneHeaders()).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getAnnuityOperationalConversionRateForDate(
    date: string
  ): Observable<AnnuityOperationalConversionRateResponse> {
    const url = Endpoints.getAnnuityOperationalConversionRateForDate(date);
    return this.apiClient.getObj(AnnuityOperationalConversionRateResponse, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetCurrentYearSmallPensionMonthlyLimit(): Observable<SmallPensionMonthlyLimit> {
    const url = Endpoints.ipGetCurrentYearSmallPensionMonthlyLimit();
    return this.apiClient.getObj(SmallPensionMonthlyLimit, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  private buildTimeZoneHeaders(): HttpHeaders {
    return new HttpHeaders({ TimeZone: TimeZoneUtils.getSystemWindowsTimeZone() });
  }
}
